import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { NextCoachingInsightService } from '../coaching-base/coaching-insight.service';
import { InsightsSummaryData } from '../coaching-insight/insight-widget/model/insights-summary-data.model';
import { KpiType } from '../coaching-insight/insight-widget/model/kpi-type.model';
import { CoachingInsightComponentAttributes } from '../coaching-insight/oa/models/coaching.global.model';
import { InsightService } from '../insight-new/insight.service';
import { PeriodType } from '../scorecard/scorecard.model';
import { Dependencies } from '../store';
import { PayloadAction } from '../store.model';

import { Mi2DataResult } from './models/mi2-data-result';
import { MyCoachingCard } from './models/my-coaching-card.model';
import { StagingKpiThreshold } from './models/staging-kpi-threshold.model';
import { WpCoachingCard } from './models/wp-coaching-card.model';
import {
    ActionType,
    Reload,
    ReloadResult,
    reloadResult,
} from './my-coaching-card.action';

export interface ChainGroup<TPayload> {
    root: TPayload;
    coachingCard?: MyCoachingCard;
    coachingCardThreshold?: StagingKpiThreshold[];
    insightSummaryData?: InsightsSummaryData;
    componentAttributes?: CoachingInsightComponentAttributes;
    mi2DataResult?: Mi2DataResult;
    wpCoachingCard?: WpCoachingCard;
}

const reload = (
    action: ActionsObservable<PayloadAction<ActionType, Reload>>,
    state: StateObservable<any>,
    deps: Dependencies,
): Observable<PayloadAction<ActionType, ReloadResult>> =>
    action.pipe(
        ofType(ActionType.MY_COACHING_CARD__ROOT__RELOAD),
        switchMap((act) => {
            const service = deps.injector.get<InsightService>(InsightService);
            const coachingInsightService =
                deps.injector.get<NextCoachingInsightService>(
                    NextCoachingInsightService,
                );

            const grp: ChainGroup<Reload> = { root: act.payload };

            const insightsSummaryDataObservable = coachingInsightService
                .retrieveInsightsSummaryData(
                    act.payload.userId,
                    act.payload.periodTypeId,
                    act.payload.effectiveDate,
                )
                .pipe(
                    tap((ret) => {
                        grp.insightSummaryData = ret;
                    }),
                );
            const mi2DataObservable = insightsSummaryDataObservable.pipe(
                switchMap((insightsData: InsightsSummaryData) => {
                    const isFlMi2 = insightsData.kpis?.some(
                        (e) =>
                            e.kpiTypeId ===
                            KpiType.FL_MANAGEMENT_IMPROVEMENT_INDEX,
                    );
                    const params = (): {
                        userId: number;
                        periodTypeId: PeriodType;
                        isFlMi2: boolean;
                    } => ({
                        periodTypeId: PeriodType.MONTHLY,
                        userId: act.payload.userId,
                        isFlMi2: isFlMi2 ?? false,
                    });

                    return of({}).pipe(service.retrieveMi2Data(params));
                }),
                tap((ret: Mi2DataResult) => {
                    grp.mi2DataResult = ret;
                }),
                catchError((err) => {
                    console.error('Error retrieving Mi2 Data', err);
                    return of(null);
                }),
            );

            const obses: [
                Observable<MyCoachingCard>,
                Observable<StagingKpiThreshold[]>,
                Observable<InsightsSummaryData>,
                Observable<CoachingInsightComponentAttributes>,
                Observable<Mi2DataResult>,
                Observable<WpCoachingCard>,
            ] = [
                of(null).pipe(
                    service.retrieveMyCoachingCard(() => ({
                        userId: act.payload.userId,
                        periodTypeId: act.payload.periodTypeId,
                        effectiveDate: act.payload.effectiveDate,
                        startDate: act.payload.startDate,
                        endDate: act.payload.endDate,
                    })),
                    tap((ret) => {
                        grp.coachingCard = ret;
                    }),
                ),
                of(null).pipe(
                    service.retrieveMyStagingKpiThresholds(() => ({})),
                    tap((ret) => {
                        grp.coachingCardThreshold = ret;
                    }),
                ),
                insightsSummaryDataObservable,
                of(null).pipe(
                    service.retrieveCoachingComponents(() => ({
                        performerUserId: act.payload.userId,
                        periodTypeId: act.payload.periodTypeId,
                    })),
                    tap((ret) => {
                        grp.componentAttributes = ret;
                    }),
                ),
                mi2DataObservable,
                of(null).pipe(
                    service.retrieveWpCoachingCard(() => ({
                        userId: act.payload.userId,
                    })),
                    tap((ret) => {
                        grp.wpCoachingCard = ret;
                    }),
                ),
            ];

            return forkJoin(obses).pipe(
                map(() => {
                    return reloadResult(
                        {
                            ...act.payload,
                            coachingCard: grp.coachingCard,
                            coachingCardThreshold: grp.coachingCardThreshold,
                            insightSummaryData: grp.insightSummaryData,
                            componentAttributes: grp.componentAttributes,
                            mi2DataResult: grp.mi2DataResult,
                            wpCoachingCard: grp.wpCoachingCard,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadResult({ ...act.payload }, act.storePath)),
                ),
            );
        }),
    );

export const coachingCardEpics = [reload];
